import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import api from "../../api/api";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Skeleton from "@mui/material/Skeleton";
import { eventBus } from "../../utils/eventBus";
import { getPracticeId } from "../../config/practice";

// Diagnoses are tracked by ICD-10 code, not by conditions_master row id: the
// problem list is rebuilt from Athena on every open, so row ids change and a
// selection kept by id silently un-highlights (QA 2026-09-29).
const dedupeByCode = (items) => {
  const seen = new Set();
  return (items || []).filter((x) => {
    const code = (x?.icd10 || "").trim();
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
};

const DiagnosisPopup = ({ isOpen, appid, onClose, onSaved, setAlertMessage, setSeverity, setOpen }) => {
  const [inputText, setInputText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuggestionSelected, setIsSuggestionSelected] = useState(false);
  const { dt } = useParams();
  // Both callers pass the visit: the chart its own row, the Orders modal its
  // appointment. Never fall back to redux appointmentInfo, which keeps the last
  // Orders modal's appointment (another patient's, from a different chart).
  const appt = appid || {};
  const [problemList, setProblemList] = useState([]);
  const [isFetchingConditions, setIsFetchingConditions] = useState(false);
  // What the visit had when the popup opened, plus this session's edits.
  const [saved, setSaved] = useState([]);
  const [isFetchingSaved, setIsFetchingSaved] = useState(false);
  const [added, setAdded] = useState({}); // icd10 -> condition_name
  const [removed, setRemoved] = useState([]); // saved codes unticked here
  const [saving, setSaving] = useState(false);
  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);

  // Robust order-date resolution: the old inline expression required a SPACE
  // in the date string, so date-only ("2026-08-20") or ISO ("...T00:00:00")
  // inputs fell through to literal `false` and the save silently failed
  // (QA: "Diagnosis are not being saved", video 1219795944).
  const resolveOrderDate = (...cands) => {
    for (const c of cands) {
      if (!c) continue;
      const d0 = String(c).split(' ')[0].split('T')[0];
      if (!isNaN(Date.parse(d0))) return d0;
    }
    return new Date().toISOString().split('T')[0];
  };
  const orderDate = resolveOrderDate(appt?.startdate, dt);

  const savedCodes = useMemo(() => new Set(saved.map((x) => x.icd10)), [saved]);
  const isSelected = (code) =>
    (savedCodes.has(code) && !removed.includes(code)) || Object.prototype.hasOwnProperty.call(added, code);

  const toggleCode = (code, name) => {
    if (!code) return;
    if (savedCodes.has(code)) {
      setRemoved((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
      return;
    }
    setAdded((prev) => {
      const next = { ...prev };
      if (next[code] !== undefined) delete next[code];
      else next[code] = name || code;
      return next;
    });
  };

  useEffect(() => {
    if (inputText.trim() === "" || isSuggestionSelected) {
      setIsSuggestionSelected(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions();
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [inputText]);

  const fetchSuggestions = async () => {
    if (!appt?.patientid) return;
    try {
      setLoading(true);
      // Patient id, not appointment id: the endpoint lists the patient's own
      // problems first ("Active") and only then the ICD-10 catalogue.
      const response = await api.post(
        `/v1/${practiseid}/orders/patient/${appt.patientid}/get-search-diagnosis`,
        { searchVal: inputText }
      );
      if (response.status === 200) {
        setSuggestions(dedupeByCode(response.data));
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (item) => {
    setInputText('');
    toggleCode(item.icd10, item.condition_name);
  };

  const fetchProblemList = async () => {
    try {
      setIsFetchingConditions(true);
      const response = await api.get(
        `/v1/${practiseid}/orders/patient/${appt.patientid}/condition`
      );
      if (response.status === 200) {
        // Keep problems that carry no ICD-10 (shown, but they can't be a
        // visit diagnosis); drop repeats of the same code.
        const seen = new Set();
        setProblemList((response.pConditions || []).filter((x) => {
          const code = (x?.icd10 || "").trim();
          if (!code) return true;
          if (seen.has(code)) return false;
          seen.add(code);
          return true;
        }));
      }
    } catch (error) {
      console.error("Error fetching patient conditions:", error);
    } finally {
      setIsFetchingConditions(false);
    }
  };

  // The visit's saved diagnoses ([{icd10, condition_name}]); throws on failure.
  const loadSavedDiagnoses = async () => {
    const res = await api.post(
      `/v1/${practiseid}/orders/patient/${appt.patientid}/get-orders`,
      { appointmentid: appt.appointmentid, orderdate: orderDate }
    );
    if (res?.status !== 200) throw new Error(res?.message || "Could not load this visit's diagnoses");
    const list = dedupeByCode(res?.data?.diagnosis).map((x) => ({
      icd10: x.icd10.trim(),
      condition_name: x.condition_name || x.icd10,
    }));
    // get-orders lists conditions_master matches first; keep the visit's own
    // order (first code = primary) from the raw appointment string.
    const stored = String(res?.data?.lovtov?.[0]?.diagnosis || "").split(",").map((c) => c.trim()).filter(Boolean);
    const pos = (c) => { const i = stored.indexOf(c); return i < 0 ? stored.length : i; };
    return list.sort((a, b) => pos(a.icd10) - pos(b.icd10));
  };

  // Bumped on every open / appointment change so a slow response for an
  // earlier open can't overwrite the current one.
  const loadSeq = useRef(0);
  const fetchSaved = async () => {
    const seq = ++loadSeq.current;
    try {
      setIsFetchingSaved(true);
      const list = await loadSavedDiagnoses();
      if (seq !== loadSeq.current) return;
      setSaved(list);
      // A code picked while this was loading may turn out to be saved already.
      setAdded((prev) => {
        const next = { ...prev };
        list.forEach((x) => { delete next[x.icd10]; });
        return next;
      });
    } catch (error) {
      if (seq !== loadSeq.current) return;
      console.error("Error fetching saved diagnoses:", error);
      setSaved([]);
    } finally {
      if (seq === loadSeq.current) setIsFetchingSaved(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setInputText("");
    setSuggestions([]);
    setAdded({});
    setRemoved([]);
    setSaved([]);
    setProblemList([]);
    if (appt?.patientid && appt?.appointmentid) {
      fetchProblemList();
      fetchSaved();
    }
  }, [isOpen, appt?.patientid, appt?.appointmentid]);

  const notify = (message, severity) => {
    setAlertMessage(message);
    setSeverity(severity);
    setOpen(true);
  };

  const handleClose = () => {
    setInputText("");
    setIsSuggestionSelected(true);
    setSuggestions([]);
    onClose();
  };

  const handleSave = async () => {
    if (saving) return;
    if (!appt?.patientid || !appt?.appointmentid) {
      notify("Open the patient's visit before saving diagnoses", "info");
      return;
    }
    setSaving(true);
    try {
      // Re-read the visit right before saving: confirm_status "E" REPLACES the
      // list, so anything saved elsewhere since this popup opened (CareIQ,
      // another user) must be carried over, minus what was unticked here.
      const current = (await loadSavedDiagnoses()).map((x) => x.icd10);
      const finalCodes = [
        ...current.filter((c) => !removed.includes(c)),
        ...Object.keys(added).filter((c) => !current.includes(c)),
      ];
      const changed = finalCodes.length !== current.length || finalCodes.some((c) => !current.includes(c));
      if (!changed) {
        notify("No changes to save", "info");
        handleClose();
        return;
      }
      if (finalCodes.length === 0) {
        // An empty list is a no-op on the server (it protects saved diagnoses
        // from status-only confirms), so clearing every code can't be saved.
        notify("Keep at least one diagnosis on the visit", "info");
        return;
      }
      // Diagnosis only: no tov/lov. The old body sent tov "2", which overwrote
      // the visit's Type of Visit with "2".
      const response = await api.post(`/v1/${practiseid}/patient/${appt.patientid}/orders/confirm`, {
        patient_name: `${appt.firstname || ''} ${appt.lastname || ''}`.trim() || appt.patient_name || "Patient",
        createdby: userConfig?.username || userConfig?.config?.username || '',
        orderdate: orderDate,
        diagnosis: finalCodes,
        appointmentid: appt.appointmentid,
        token_no: 0,
        confirm_status: "E",
        update_status: "C",
      });
      if (response.status === 200) {
        eventBus.emit("fetchSummary");
        if (onSaved) onSaved(finalCodes);
        notify(response.message || "Diagnosis saved", "success");
        handleClose();
      } else {
        notify(response.message || "Diagnosis could not be saved", "info");
      }
    } catch (error) {
      console.error("Error saving diagnosis:", error);
      notify(error?.response?.data?.message || "Diagnosis could not be saved. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Chips for "On this visit": saved codes (ticked or unticked) then new picks.
  const visitChips = [
    ...saved.map((x) => ({ ...x, isNew: false })),
    ...Object.entries(added).map(([icd10, condition_name]) => ({ icd10, condition_name, isNew: true })),
  ];

  const chipClass = (selected) =>
    `cursor-pointer font-poppins text-xs text-center rounded-full border-[1px] border-gray-200 flex items-center justify-center transition-colors duration-300 ease-in-out ${
      selected ? "bg-[#e17954] text-white" : "bg-[#E5E7EB] text-[#1D2951]"
    } px-3 h-[35px] m-2`;

  return (
    isOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="relative flex flex-col gap-4 w-[60%] max-h-[80vh] bg-white px-4 pb-6 rounded-lg shadow-lg text-left overflow-hidden">
          <div>
            <div className="w-full flex items-center justify-between mt-2">
              <span className="ml-2 font-semibold text-[#1D2951] text-base text-center">
                Diagnosis
              </span>
              <button
                className="text-gray-500 hover:text-gray-700 text-[30px]"
                onClick={handleClose}
              >
                &times;
              </button>
            </div>
            <div className="h-px bg-[#D0D5DD]"></div>
          </div>

          <div>
            <label className="text-[#111827] my-0 px-2">On this visit</label>
            <div className="w-full flex flex-wrap max-h-[160px] overflow-y-auto custom-scrollbar">
              {isFetchingSaved ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width={100}
                    height={35}
                    style={{ margin: 8, borderRadius: "20px" }}
                  />
                ))
              ) : visitChips.length === 0 ? (
                <div className="text-sm text-gray-500 px-2 py-2">
                  No diagnoses on this visit yet. Pick from the problem list or search.
                </div>
              ) : (
                visitChips.map((item) => {
                  const selected = isSelected(item.icd10);
                  return (
                    <div
                      key={item.icd10}
                      onClick={() => toggleCode(item.icd10, item.condition_name)}
                      title={selected ? "Remove from this visit" : "Add back to this visit"}
                      className={`${chipClass(selected)} gap-2 ${selected ? "" : "line-through"}`}
                    >
                      <span>{item.condition_name}</span>
                      {selected && <span aria-hidden="true">&times;</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex w-full flex-col sm:flex-row md:flex-row lg:flex-row">
            <div>
              <div className="relative w-full max-w-md mr-2">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="rounded-[16px] w-[340px] border border-[#D5D5D5] bg-[#F5F6FA] pl-10 pr-16 py-1 text-base outline-none"
                  placeholder="Search for diagnosis"
                />
              </div>
              <div className="w-full mt-2">
                {loading ? (
                  <div className="text-sm text-gray-500">Getting suggestions...</div>
                ) : inputText.trim() !== "" && suggestions.length === 0 ? (
                  <div className="text-center text-sm text-gray-500">
                    Suggestions not found!
                  </div>
                ) : (
                  <div className="w-full flex flex-wrap gap-1 max-h-[250px] overflow-y-auto custom-scrollbar">
                    {suggestions.map((item) => (
                      <div
                        key={item.icd10}
                        onClick={() => handleSuggestionClick(item)}
                        className={`cursor-pointer font-poppins text-xs text-center rounded-full flex items-center justify-center px-3 py-2 my-1 ${
                          isSelected(item.icd10)
                            ? "bg-[#e17954] text-white"
                            : "bg-[#E5E7EB] text-[#1D2951]"
                        }`}
                      >
                        {item.condition_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-[#111827] my-0 px-2">Problem List</label>
              <div className="w-full overflow-y-auto custom-scrollbar flex-grow max-h-[250px]">
                <div className="w-full flex flex-wrap">
                  {isFetchingConditions ? (
                    Array.from({ length: 6 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width={100}
                        height={35}
                        style={{ margin: 8, borderRadius: "20px" }}
                      />
                    ))
                  ) : (
                    problemList.map((item) => {
                      const code = (item.icd10 || "").trim();
                      return code ? (
                        <div
                          key={code}
                          onClick={() => toggleCode(code, item.condition_name)}
                          className={chipClass(isSelected(code))}
                        >
                          {item.condition_name}
                        </div>
                      ) : (
                        <div
                          key={`noicd-${item.id}`}
                          title="This problem has no ICD-10 code, so it can't be added to the visit"
                          className={`${chipClass(false)} opacity-50 cursor-not-allowed`}
                        >
                          {item.condition_name}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex justify-center mt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`rounded py-2 w-full px-4 text-[12px] bg-[#18181B] text-[#FFFFFF] ${saving ? "opacity-60 cursor-wait" : ""}`}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default DiagnosisPopup;
